import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";

const AllProducts = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const category = queryParams.get("category");

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch products from API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch("/api/products");
        const data = await response.json();
        setProducts(data.products || []);
      } catch (error) {
        console.error("Error fetching products:", error);
        toast.error("Failed to load products. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Filter products based on category and search
  const filteredProducts = products.filter((product) => {
    const matchesCategory = category ? product.collectionName === category : true;
    const matchesSearch = product.model.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);

    if (e.target.value) {
      toast("Showing results for your search...", {
        description: "You can further filter by category.",
        duration: 3000,
        position: "top-right",
        style: {
          backgroundColor: "#262126",
          color: "#fff",
          padding: "10px 20px",
          borderRadius: "10px",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          fontSize: "14px",
        },
      });
    }
  };

  if (loading) {
    return <p className="text-center mt-10">Loading products...</p>;
  }

  return (
    <div className="container mx-auto p-6">
      {/* Search Input */}
      <div className="mb-6">
        <input
          type="text"
          placeholder="Search products..."
          value={searchTerm}
          onChange={handleSearchChange}
          className="border border-gray-300 px-4 py-2 rounded-xl w-full md:w-1/2 mx-auto focus:outline-none focus:ring-2 focus:ring-orange-400 transition"
        />
      </div>

      <h2 className="text-2xl font-bold poppins my-6 text-center">
        {category ? `${category} Collection` : "All Products"}
      </h2>

      {/* Products Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {filteredProducts.map((product) => {
          const img = product.variants?.[0]?.images?.[0]?.src || "/placeholder.jpg";
          const price = product.finalPrice;

          return (
            <div
              key={product._id}
              onClick={() => navigate(`/product/${product._id}`, { state: { product } })}
              className="bg-white rounded-2xl p-4 cursor-pointer shadow-md hover:shadow-2xl transition-transform duration-300 transform hover:-translate-y-2 hover:scale-105 flex flex-col items-center"
            >
              {/* Image Container */}
              <div className="w-full aspect-[1/1] rounded-2xl overflow-hidden flex justify-center items-center bg-gray-50">
                <img
                  src={img}
                  alt={product.model}
                  className="w-full h-full object-contain transition-transform duration-500 ease-in-out hover:scale-110"
                />
              </div>

              {/* Product Info */}
              <h3 className="mt-4 text-lg font-semibold text-center">{product.model}</h3>
              <div className="text-gray-600 text-sm mt-1 transition-all duration-300">
                {product.sale?.isOnSale ? (
                  <div className="flex flex-col items-center">
                    <span className="text-red-500 font-bold">
                      {product.sale.finalPrice.toLocaleString()} DT
                    </span>
                    <span className="text-gray-400 text-sm line-through">
                      {product.price.toLocaleString()} DT
                    </span>
                  </div>
                ) : (
                  <span className="text-gray-800">
                    {(product.sale?.finalPrice || product.price).toLocaleString()} DT
                  </span>
                )}
              </div>
              {product.collectionName && (
                <p className="text-gray-400 text-xs mt-1">{product.collectionName}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AllProducts;
